import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, ChevronRight, Trash2, Sparkles } from "lucide-react";
import { relativeTime } from "@/lib/format";
import { toast } from "sonner";

export default function WizardList() {
    const { user } = useAuth();
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        try { const { data } = await api.get("/onboarding/drafts"); setDrafts(data); }
        catch { setDrafts([]); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const startNew = async () => {
        try {
            const { data } = await api.post("/onboarding/drafts", {});
            navigate(`/wizard/${data.id}`);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Fshi këtë draft?")) return;
        try { await api.delete(`/onboarding/drafts/${id}`); await load(); }
        catch { toast.error("Dështoi"); }
    };

    const progress = (d) => Math.round(((d.completed_steps?.length || 0) / 5) * 100);

    return (
        <>
            <Topbar
                title="Setup Wizard"
                subtitle="Konfiguro hotelet hap pas hapi"
                actions={<button onClick={startNew} className="akm-btn-primary" data-testid="start-wizard-btn"><Plus size={14} /> Fillo hotel të ri</button>}
            />
            <div className="flex-1 overflow-auto p-6">
                {!loading && drafts.length === 0 ? (
                    <div className="mx-auto max-w-md py-16 text-center">
                        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                            <Sparkles size={20} />
                        </div>
                        <div className="text-base font-semibold tracking-tight">S'ka drafte ende</div>
                        <p className="mt-1 text-sm text-muted-foreground">
                            Përdor setup wizard-in për të konfiguruar një hotel të ri në Akomoda — pronën, dhomat, llojet & çmimet, kanalet, dhe booking engine në 5 hapa të shpejtë (nën 10 minuta).
                        </p>
                        <button onClick={startNew} className="akm-btn-primary mt-6" data-testid="empty-start-btn"><Plus size={14} /> Fillo hotel të ri</button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                        {drafts.map((d) => (
                            <div key={d.id} className="akm-card p-4" data-testid={`draft-${d.id}`}>
                                <div className="flex items-start justify-between">
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate text-sm font-semibold tracking-tight">{d.hotel_name || "Hotel i ri"}</div>
                                        <div className="mt-0.5 text-[11px] text-muted-foreground">{d.data?.step1?.city || "—"}, {d.data?.step1?.country || "—"}</div>
                                    </div>
                                    <StatusBadge status={d.status === "completed" ? "active" : "trial"} label={d.status} />
                                </div>
                                <div className="mt-3">
                                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                                        <span>Hapi {Math.min(d.current_step, 5)} nga 5</span>
                                        <span>{progress(d)}% plotësuar</span>
                                    </div>
                                    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                                        <div className="h-full bg-primary transition-all" style={{ width: `${progress(d)}%` }} />
                                    </div>
                                </div>
                                <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground">
                                    <span>Përditësuar {relativeTime(d.updated_at)}</span>
                                </div>
                                <div className="mt-3 flex gap-2">
                                    <button onClick={() => navigate(`/wizard/${d.id}`)} className="akm-btn-primary flex-1 text-[12px]" data-testid={`resume-${d.id}`}>
                                        {d.status === "completed" ? "Shiko" : "Vazhdo"} <ChevronRight size={12} />
                                    </button>
                                    <button onClick={() => remove(d.id)} className="akm-btn-ghost text-red-700"><Trash2 size={12} /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}
